import { useCallback, useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ActivityReply } from "../../../api/generated";
import {
    getActivityReply,
    addCommentOnActivityReply,
    updateLastWatchedAt,
    getSignedUrlForCommentImages,
} from "../../../api/activityReply";
import { useAuth } from "../../auth/hooks/useAuth";
import { showToast } from "@twioku/common_components";
import { useSyncActivityReplyCache } from "./useSyncActivityReplyCache";
import { isRecordStringString } from "../../../libs/helper";
import { useUploadFileBySignedUrl } from "../../../utils/hooks/useUploadFileBySignedUrl";
import { COMMON_ERROR_MESSAGE } from "../../../libs/constants";
export const useActivityReply = (activityReplyId: string) => {
    const { csrfToken } = useAuth();
    const [newCommentText, setNewCommentText] = useState<string>("");
    const [newCommentImages, setNewCommentImages] = useState<Array<File>>([]);
    const [isLoadingAddComment, setIsLoadingAddComment] =
        useState<boolean>(false);

    const { syncActivityReplyCache } = useSyncActivityReplyCache();

    const {
        data: activityReply,
        isLoading,
        isSuccess,
    } = useQuery<ActivityReply>({
        queryKey: ["activityReply", activityReplyId],
        queryFn: () => getActivityReply(activityReplyId),
    });

    const { mutate: addCommentMutate } = useMutation({
        mutationFn: () => addComment(),
        onSuccess(data) {
            if (!data) return;
            syncActivityReplyCache(data);
        },
    });

    const uploadImageBySignedUrl = useUploadFileBySignedUrl();

    const uploadImage = useCallback(
        async (uploadImage: File[], csrfToken: string) => {
            if (uploadImage.length === 0) return [];

            const signedUrlResults = await getSignedUrlForCommentImages(
                uploadImage,
                csrfToken
            );

            await Promise.all(
                signedUrlResults.map(async (signedUrlResult) => {
                    const file = uploadImage.find(
                        (image) => image.name === signedUrlResult.fileName
                    );
                    if (!file) return;
                    if (!isRecordStringString(signedUrlResult.fields)) return;
                    await uploadImageBySignedUrl(
                        file,
                        signedUrlResult.fields,
                        signedUrlResult.signedUrl
                    );
                })
            );

            return signedUrlResults.map((signedUrl) => signedUrl.filePath);
        },
        [uploadImageBySignedUrl]
    );

    const addComment = async (): Promise<ActivityReply | void> => {
        if (newCommentText.length <= 0 && newCommentImages.length <= 0) return;

        setIsLoadingAddComment(true);

        try {
            const filePaths = await uploadImage(newCommentImages, csrfToken);
            const activityReply = await addCommentOnActivityReply(
                activityReplyId,
                newCommentText,
                filePaths,
                csrfToken
            );
            setNewCommentText("");
            setCommentImages([]);

            return activityReply;
        } catch (error) {
            showToast(COMMON_ERROR_MESSAGE, "error");
            console.error(error);
        } finally {
            setIsLoadingAddComment(false);
        }
    };

    const { mutate: updateLastWatchedAtMutate } = useMutation({
        mutationFn: (param: { activityReplyId: string; csrfToken: string }) =>
            updateLastWatchedAt(param.activityReplyId, param.csrfToken),
        onSuccess(data: ActivityReply) {
            syncActivityReplyCache(data);
        },
    });

    const setCommentImages = (
        files: Array<File>
    ): { isSuccess: boolean; message: string } => {
        const imageCountLimit = 4;
        if (files.length > imageCountLimit) {
            return {
                isSuccess: false,
                message: "選択できる画像は最大4枚です。",
            };
        }

        const sizeLimitMb = 6;
        for (let i = 0; i < files.length; i++) {
            const allowExtensions = ".(jpeg|jpg|png|gif)$";
            if (!files[i].name.match(allowExtensions)) {
                return {
                    isSuccess: false,
                    message: "jpeg, jpg, png, gifファイルを選択してください。",
                };
            }

            const sizeLimit = 1024 * 1024 * sizeLimitMb;
            if (files[i].size > sizeLimit) {
                return {
                    isSuccess: false,
                    message: "ファイルサイズは6MB以下にしてください。",
                };
            }
        }

        setNewCommentImages(files);
        return {
            isSuccess: true,
            message: "",
        };
    };

    useEffect(() => {
        if (isSuccess) {
            updateLastWatchedAtMutate({ activityReplyId, csrfToken });
        }
    }, [isSuccess, updateLastWatchedAtMutate, activityReplyId, csrfToken]);

    return {
        activityReply,
        newCommentText,
        newCommentImages,
        setNewCommentText,
        addComment: addCommentMutate,
        setCommentImages,
        isLoading,
        isLoadingAddComment,
    };
};
